"use client";

import { motion } from "framer-motion";
import { useTranslation } from "next-i18next";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface LatestNewsSectionProps {
  latestNews: {
    title: string;
    items: Array<{
      title: string;
      imgUrl: string;
      href?: string;
    }>;
  };
}

const LatestNewsSection = ({ latestNews }: LatestNewsSectionProps) => {
  const { t } = useTranslation("common");
  if (!latestNews?.items || latestNews.items.length === 0) return null;

  return (
    <section className="bg-white py-[3rem] md:py-[5rem] px-[1rem] md:px-[2rem] w-full overflow-hidden">
      <div className="mx-auto max-w-[120rem] w-full">
        <motion.h2
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="text-[2rem] sm:text-[2.5rem] md:text-[3rem] lg:text-[4rem] font-bold text-tertiary text-center mb-[2rem] md:mb-[4rem] px-[1rem]"
        >
          {latestNews?.title}
        </motion.h2>

        <div className="relative px-[2rem] md:px-[3rem]">
          <Swiper
            modules={[Navigation, Pagination]}
            navigation={{
              nextEl: ".news-button-next",
              prevEl: ".news-button-prev",
            }}
            pagination={{
              clickable: true,
              dynamicBullets: true,
            }}
            breakpoints={{
              640: {
                slidesPerView: 1,
                spaceBetween: 20,
              },
              768: {
                slidesPerView: 2,
                spaceBetween: 30,
              },
              1024: {
                slidesPerView: 3,
                spaceBetween: 30,
              },
            }}
            className="pb-[4rem] news-swiper "
          >
            {latestNews.items.map((item, index) => (
              <SwiperSlide key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.45,
                    delay: Math.min(index * 0.025, 0.15),
                  }}
                  className="bg-white rounded-[1rem] overflow-hidden with-box-shadow transition-shadow h-full flex flex-col m-2 my-10 mt-5"
                >
                  <Link
                    href={item.href || "/news"}
                    className="flex flex-col flex-1 w-full"
                  >
                    <div className="relative w-full aspect-[3/2] shrink-0">
                      <Image
                        src={item.imgUrl || ""}
                        alt={item.title}
                        fill
                        className="object-cover"
                      />
                    </div>
                    <div className="p-[1.5rem] md:p-[2rem] flex-1 flex flex-col">
                      <h3
                        className="text-[1.1rem] sm:text-[1.3rem] md:text-[1.5rem] font-bold text-tertiary overflow-hidden leading-[1.6] min-h-[5.28rem] md:min-h-[7.2rem] mb-[1.5rem]"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {item.title}
                      </h3>
                      <span className="self-start inline-flex items-center justify-center text-[1rem] bg-secondary text-white font-bold rounded-[0.5rem] px-[1.5rem] py-[0.75rem] mt-auto">
                        {t("blogsPage.readMore")}
                      </span>
                    </div>
                  </Link>
                </motion.div>
              </SwiperSlide>
            ))}
          </Swiper>

          {/* Custom Navigation */}
          <button className="news-button-prev absolute left-[-0.5rem] md:left-[-1rem] top-1/2 -translate-y-1/2 z-20 w-[2.5rem] h-[2.5rem] md:w-[3rem] md:h-[3rem] flex items-center justify-center bg-tertiary hover:bg-[#0a4384] rounded-full transition-colors shadow-lg">
            <ChevronLeft className="text-white text-[1.5rem] md:text-[2rem]" />
          </button>
          <button className="news-button-next absolute right-[-0.5rem] md:right-[-1rem] top-1/2 -translate-y-1/2 z-20 w-[2.5rem] h-[2.5rem] md:w-[3rem] md:h-[3rem] flex items-center justify-center bg-tertiary hover:bg-[#0a4384] rounded-full transition-colors shadow-lg">
            <ChevronRight className="text-white text-[1.5rem] md:text-[2rem]" />
          </button>
        </div>

        {/* Custom Pagination Styles */}
        <style jsx global>{`
          .news-swiper .swiper-wrapper {
            align-items: stretch;
          }
          .news-swiper .swiper-slide {
            height: auto;
            display: flex;
          }
          .news-swiper .swiper-slide > div {
            height: auto;
            flex: 1;
          }
          .news-swiper .swiper-pagination {
            margin-top: 1rem !important;
          }
          .news-swiper .swiper-pagination-bullet {
            width: 0.75rem !important;
            height: 0.75rem !important;
            background: rgba(26, 43, 107, 0.3) !important;
            opacity: 1 !important;
            margin: 0 0.25rem !important;
          }
          .news-swiper .swiper-pagination-bullet-active {
            background: #1a2b6b !important;
            width: 1.5rem !important;

            border-radius: 0.375rem !important;
          }
        `}</style>
      </div>
    </section>
  );
};

export default LatestNewsSection;
